import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPencil, faWifi } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../../components/modal/modal';
import axiosInstance from '../../../AxiosInstance';

const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: '6px',
    border: '1px solid #CBD5E1', fontSize: '13px',
    outline: 'none', marginBottom: '4px', boxSizing: 'border-box',
};
const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: '#475569',
    display: 'block', marginBottom: '3px', marginTop: '10px',
};

const EMPTY_FORM = { name: '', mac_address: '', rssi_at_1m: -65, tx_power: -59, tx_interval: 1000, description: '' };

/**
 * IndoorBeaconsTable
 * Props:
 *   beacons    - IndoorBeacon[]
 *   projectId  - number
 *   onRefresh  - () => void
 */
export default function IndoorBeaconsTable({ beacons = [], projectId, onRefresh }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null);   // beacon id
    const [deleteTarget, setDeleteTarget] = useState(null);

    const filtered = beacons.filter(b => {
        const q = search.toLowerCase();
        return !q || b.name?.toLowerCase().includes(q) || b.mac_address?.toLowerCase().includes(q);
    });

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setShowModal(true);
    };

    const openEdit = (beacon) => {
        setEditing(beacon.id);
        setForm({
            name: beacon.name || '',
            mac_address: beacon.mac_address || '',
            rssi_at_1m: beacon.rssi_at_1m ?? -65,
            tx_power: beacon.tx_power ?? -59,
            tx_interval: beacon.tx_interval ?? 1000,
            description: beacon.description || '',
        });
        setShowModal(true);
    };

    const handleSave = () => {
        const payload = { ...form, project: projectId, rssi_at_1m: parseInt(form.rssi_at_1m), tx_power: parseInt(form.tx_power), tx_interval: parseInt(form.tx_interval) };
        const req = editing
            ? axiosInstance.put(`indoor_beacon/${editing}/`, payload)
            : axiosInstance.post('indoor_beacon/', payload);
        req.then(() => { setShowModal(false); onRefresh(); });
    };

    const handleDelete = () => {
        axiosInstance.delete(`indoor_beacon/${deleteTarget.id}/`).then(() => { setDeleteTarget(null); onRefresh(); });
    };

    const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('gps.indoor.searchBeacon')}
                    style={{ ...inputStyle, marginBottom: 0, maxWidth: '240px' }}
                />
                <button onClick={openCreate} style={{ background: '#4F46E5', border: 'none', borderRadius: '6px', padding: '7px 14px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faPlus} /> {t('gps.indoor.newBeacon')}
                </button>
            </div>

            {/* Table */}
            {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94A3B8', padding: '24px', fontSize: '13px' }}>
                    {t('gps.indoor.noBeaconsInProject')}
                </div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ background: '#F8FAFC', color: '#475569', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>MAC</th>
                            <th style={thStyle}>{t('gps.indoor.rssiAt1m')}</th>
                            <th style={thStyle}>{t('gps.indoor.txPower')}</th>
                            <th style={thStyle}>{t('gps.indoor.txInterval')}</th>
                            <th style={thStyle}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(b => (
                            <tr key={b.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                <td style={tdStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                        <FontAwesomeIcon icon={faWifi} style={{ color: '#4F46E5', fontSize: '11px' }} />
                                        <span style={{ fontWeight: '600', color: '#1E293B' }}>{b.name}</span>
                                    </div>
                                </td>
                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px' }}>{b.mac_address}</td>
                                <td style={tdStyle}>{b.rssi_at_1m} dBm</td>
                                <td style={tdStyle}>{b.tx_power} dBm</td>
                                <td style={tdStyle}>{b.tx_interval} ms</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                    <button onClick={() => openEdit(b)} style={iconBtn('#4F46E5')}><FontAwesomeIcon icon={faPencil} /></button>
                                    <button onClick={() => setDeleteTarget(b)} style={iconBtn('#EF4444')}><FontAwesomeIcon icon={faTrash} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Create/Edit modal */}
            <Modal show={showModal} setter={setShowModal} name={editing ? t('gps.indoor.editBeacon') : t('gps.indoor.newBeacon')} buttonName={t('common.save', 'Save')} onClick={handleSave}>
                <label style={labelStyle}>{t('gps.indoor.beaconName')}</label>
                <input value={form.name} onChange={e => setF('name', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.macAddress')}</label>
                <input value={form.mac_address} onChange={e => setF('mac_address', e.target.value)} style={inputStyle} placeholder="AA:BB:CC:DD:EE:FF" />
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '4px' }}>{t('gps.indoor.macAddressHint')}</div>
                <label style={labelStyle}>{t('gps.indoor.rssiAt1m')}</label>
                <input type="number" value={form.rssi_at_1m} onChange={e => setF('rssi_at_1m', e.target.value)} style={inputStyle} />
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '4px' }}>{t('gps.indoor.rssiAt1mHint')}</div>
                <label style={labelStyle}>{t('gps.indoor.txPower')}</label>
                <input type="number" value={form.tx_power} onChange={e => setF('tx_power', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.txInterval')}</label>
                <input type="number" value={form.tx_interval} onChange={e => setF('tx_interval', e.target.value)} style={inputStyle} />
                <label style={labelStyle}>{t('gps.indoor.description')}</label>
                <input value={form.description} onChange={e => setF('description', e.target.value)} style={inputStyle} />
            </Modal>

            {/* Delete confirm modal */}
            <Modal show={!!deleteTarget} setter={() => setDeleteTarget(null)} name={t('gps.indoor.deleteBeacon')} buttonName={t('common.delete', 'Delete')} onClick={handleDelete} danger>
                <p style={{ fontSize: '14px', color: '#475569' }}>
                    Delete beacon <strong>{deleteTarget?.name}</strong> ({deleteTarget?.mac_address})?
                </p>
            </Modal>
        </div>
    );
}

const thStyle = { padding: '8px 10px', textAlign: 'left', fontWeight: '700' };
const tdStyle = { padding: '8px 10px', color: '#334155' };
const iconBtn = (color) => ({
    background: 'none', border: 'none', cursor: 'pointer', color, fontSize: '13px', padding: '3px 6px',
});
